import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  createOpeningHour,
  listOpeningHours,
  updateOpeningHour,
} from '../../companies/services/companyService';
import { CompanyOpeningHour, CreateCompanyOpeningHourRequest } from '../../companies/types/Company';
import { TimeInput24 } from '../../../shared/components/TimeInput24';
import styles from './WorkspaceSetupPage.module.css';

export interface SetupBusinessHoursHandle {
  /** Saves edited hours. Returns false when the step should stay put. */
  saveIfNeeded: () => Promise<boolean>;
}

interface SetupBusinessHoursStepProps {
  canManage: boolean;
}

const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;
const DEFAULT_DAYS = [1, 2, 3, 4, 5];
const DEFAULT_START = '09:00';
const DEFAULT_END = '17:00';

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

interface HoursDraft {
  days: number[];
  start: string;
  end: string;
}

const defaultDraft = (): HoursDraft => ({
  days: [...DEFAULT_DAYS],
  start: DEFAULT_START,
  end: DEFAULT_END,
});

const dayKey = (days: readonly number[]): string => [...days].sort((left, right) => left - right).join(',');

const sameDraft = (left: HoursDraft, right: HoursDraft): boolean =>
  left.start === right.start && left.end === right.end && dayKey(left.days) === dayKey(right.days);

const clock = (value: string): string => value.slice(0, 5);

const draftFromHour = (hour: CompanyOpeningHour): HoursDraft => ({
  days: hour.daysOfWeek.length > 0 ? hour.daysOfWeek : [...DEFAULT_DAYS],
  start: clock(hour.startTime) || DEFAULT_START,
  end: clock(hour.endTime) || DEFAULT_END,
});

const isCompanyWideWeekly = (hour: CompanyOpeningHour): boolean =>
  hour.isEnabled && !hour.locationId && hour.repeatPattern === 'weekly';

const toRequest = (draft: HoursDraft): CreateCompanyOpeningHourRequest => ({
  repeatPattern: 'weekly',
  isAllDay: false,
  daysOfWeek: DAY_ORDER.filter(day => draft.days.includes(day)),
  startTime: `${draft.start}:00`,
  endTime: `${draft.end}:00`,
  isEnabled: true,
});

const isClock = (value: string): boolean => /^\d{2}:\d{2}$/.test(value);

export const SetupBusinessHoursStep = React.forwardRef<SetupBusinessHoursHandle, SetupBusinessHoursStepProps>(
  ({ canManage }, ref) => {
    const { t } = useTranslation('workspaces');
    const [draft, setDraft] = React.useState<HoursDraft>(defaultDraft);
    const [baseline, setBaseline] = React.useState<HoursDraft>(defaultDraft);
    const [existingId, setExistingId] = React.useState<string | null>(null);
    const [hasOtherHours, setHasOtherHours] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [loadFailed, setLoadFailed] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
      let cancelled = false;
      setLoading(true);
      setLoadFailed(false);
      setError(null);

      void listOpeningHours()
        .then(hours => {
          if (cancelled) {
            return;
          }

          const target = hours.find(isCompanyWideWeekly) ?? null;
          const next = target ? draftFromHour(target) : defaultDraft();
          setExistingId(target?.id ?? null);
          setHasOtherHours(hours.some(hour => hour.id !== target?.id));
          setDraft(next);
          setBaseline(next);
        })
        .catch(loadError => {
          if (!cancelled) {
            setLoadFailed(true);
            setError(loadError instanceof Error ? loadError.message : t('setup.hours.loadFailed'));
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });

      return () => {
        cancelled = true;
      };
    }, [t]);

    React.useImperativeHandle(ref, () => ({
      saveIfNeeded: async () => {
        if (loading) {
          setError(t('setup.hours.loading'));
          return false;
        }

        if (loadFailed) {
          setError(t('setup.hours.loadFailed'));
          return false;
        }

        const dirty = !sameDraft(draft, baseline);
        const shouldSave = existingId ? dirty : dirty || !hasOtherHours;
        if (!shouldSave) {
          return true;
        }

        if (!canManage) {
          setError(t('setup.hours.noPermission'));
          return false;
        }

        if (draft.days.length === 0) {
          setError(t('setup.hours.daysRequired'));
          return false;
        }

        if (!isClock(draft.start) || !isClock(draft.end)) {
          setError(t('setup.hours.timesRequired'));
          return false;
        }

        if (draft.start >= draft.end) {
          setError(t('setup.hours.endAfterStart'));
          return false;
        }

        setSaving(true);
        setError(null);
        try {
          const request = toRequest(draft);
          const saved = existingId
            ? await updateOpeningHour(existingId, request)
            : await createOpeningHour(request);
          if (saved?.id) {
            setExistingId(saved.id);
          }
          setBaseline(draft);
          setHasOtherHours(false);
          return true;
        } catch (saveError) {
          setError(saveError instanceof Error ? saveError.message : t('setup.hours.saveFailed'));
          return false;
        } finally {
          setSaving(false);
        }
      },
    }), [baseline, canManage, draft, existingId, hasOtherHours, loadFailed, loading, t]);

    const toggleDay = (day: number) => {
      setDraft(current => ({
        ...current,
        days: current.days.includes(day)
          ? current.days.filter(item => item !== day)
          : [...current.days, day],
      }));
    };

    const disabled = !canManage || loading || saving;

    return (
      <div>
        <p className={styles.lead}>{t('setup.hours.lead')}</p>
        {loading ? <p className={styles.hint}>{t('setup.hours.loading')}</p> : null}
        {error ? <p className={styles.error}>{error}</p> : null}
        {!canManage ? <p className={styles.hint}>{t('setup.hours.noPermission')}</p> : null}
        {existingId ? <p className={styles.hint}>{t('setup.hours.alreadySet')}</p> : null}
        {!existingId && hasOtherHours ? <p className={styles.hint}>{t('setup.hours.otherHours')}</p> : null}

        <p className={styles.fieldLabel}>{t('setup.hours.daysLabel')}</p>
        <div className={styles.dayRow}>
          {DAY_ORDER.map((day, index) => {
            const selected = draft.days.includes(day);
            return (
              <button
                key={day}
                type="button"
                className={`${styles.dayButton} ${selected ? styles.dayButtonSelected : ''}`}
                aria-pressed={selected}
                disabled={disabled}
                onClick={() => toggleDay(day)}
              >
                {t(`setup.hours.days.${DAY_KEYS[index]}`)}
              </button>
            );
          })}
        </div>

        <div className={styles.hoursRow}>
          <label className={styles.field}>
            <span>{t('setup.hours.start')}</span>
            <TimeInput24
              value={draft.start}
              onChange={start => setDraft(current => ({ ...current, start }))}
              disabled={disabled}
            />
          </label>
          <label className={styles.field}>
            <span>{t('setup.hours.end')}</span>
            <TimeInput24
              value={draft.end}
              onChange={end => setDraft(current => ({ ...current, end }))}
              disabled={disabled}
            />
          </label>
        </div>

        <p className={styles.hint}>{t('setup.hours.skipKeeps')}</p>
        <p className={styles.moreLink}>
          <Link to="/admin/companies?tab=business-hours">{t('setup.hours.more')}</Link>
        </p>
      </div>
    );
  },
);

SetupBusinessHoursStep.displayName = 'SetupBusinessHoursStep';
